import React from 'react';

export type MockupType =
  | 'observations'
  | 'assessments'
  | 'curriculum'
  | 'reports'
  | 'dashboard'
  | 'guardian';

interface MockupPreviewProps {
  type: MockupType;
  className?: string;
}

/**
 * Renders a stylised, screenshot-like UI mockup for marketing pages.
 * No external image files are required — each mockup is composed of divs
 * styled with Tailwind so they look like real product screenshots without
 * needing to ship bitmap assets.
 *
 * Each mockup is framed with a browser-style chrome (three coloured dots
 * + URL pill) and has realistic South African context (names, CAPS
 * learning areas, subjects, currency).
 */
const MockupPreview: React.FC<MockupPreviewProps> = ({ type, className = '' }) => {
  return (
    <div
      data-testid={`mockup-${type}`}
      className={`relative rounded-2xl overflow-hidden shadow-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 ${className}`}
    >
      {/* Browser chrome */}
      <div className="bg-gray-100 dark:bg-gray-800 px-4 py-2.5 flex items-center space-x-2 border-b border-gray-200 dark:border-gray-700">
        <div className="w-2.5 h-2.5 rounded-full bg-red-400" aria-hidden="true" />
        <div className="w-2.5 h-2.5 rounded-full bg-yellow-400" aria-hidden="true" />
        <div className="w-2.5 h-2.5 rounded-full bg-green-400" aria-hidden="true" />
        <div className="flex-1 mx-3">
          <div className="bg-white dark:bg-gray-900 rounded-md px-3 py-1 text-[10px] text-gray-400 dark:text-gray-500 text-center truncate">
            app.sprout.education/{type}
          </div>
        </div>
      </div>

      {/* Content area */}
      <div className="p-4 bg-gradient-to-br from-gray-50 to-white dark:from-gray-900 dark:to-gray-900 aspect-[4/3] overflow-hidden">
        {type === 'observations' && <ObservationsMock />}
        {type === 'assessments' && <AssessmentsMock />}
        {type === 'curriculum' && <CurriculumMock />}
        {type === 'reports' && <ReportsMock />}
        {type === 'dashboard' && <DashboardMock />}
        {type === 'guardian' && <GuardianMock />}
      </div>
    </div>
  );
};

/* ─── Individual mockups ──────────────────────────────────────────────── */

const observations = [
  {
    initials: 'TM',
    name: 'Thandi Mokoena',
    snippet: 'Thandi completed a 12-piece puzzle independently and helped Sipho with his shape sorter.',
    time: 'Today, 09:24',
    area: 'Cognitive',
    color: 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300',
  },
  {
    initials: 'SN',
    name: 'Sipho Ndlovu',
    snippet: 'Counted to 20 in isiZulu and English during circle time. Showing strong number recognition.',
    time: 'Today, 08:55',
    area: 'Mathematics',
    color: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300',
  },
  {
    initials: 'AV',
    name: 'Anika van der Merwe',
    snippet: 'Shared her painting with the class and described the colours she chose.',
    time: 'Yesterday',
    area: 'Language',
    color: 'bg-purple-100 text-purple-700 dark:bg-purple-900/40 dark:text-purple-300',
  },
];

const ObservationsMock: React.FC = () => (
  <div className="space-y-2.5">
    <div className="flex items-center justify-between mb-1">
      <h4 className="text-sm font-semibold text-gray-900 dark:text-white">Recent Observations</h4>
      <span className="text-[10px] text-gray-400">Grade R · 4 of 24</span>
    </div>
    {observations.map(obs => (
      <div
        key={obs.initials}
        className="flex items-start gap-2.5 rounded-lg bg-white dark:bg-gray-800 border border-gray-100 dark:border-gray-700 p-2.5 shadow-sm"
      >
        <div className="w-8 h-8 rounded-full bg-primary-100 dark:bg-primary-900/40 flex items-center justify-center text-[11px] font-semibold text-primary-700 dark:text-primary-300 flex-shrink-0">
          {obs.initials}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between gap-2">
            <p className="text-xs font-medium text-gray-900 dark:text-white truncate">{obs.name}</p>
            <span className="text-[9px] text-gray-400 dark:text-gray-500 flex-shrink-0">{obs.time}</span>
          </div>
          <p className="text-[10px] text-gray-500 dark:text-gray-400 mt-0.5 line-clamp-2">{obs.snippet}</p>
          <span className={`inline-block mt-1 px-1.5 py-0.5 rounded-full text-[9px] font-medium ${obs.color}`}>
            {obs.area}
          </span>
        </div>
      </div>
    ))}
  </div>
);

const assessmentsRows = [
  { name: 'Thandi Mokoena', subject: 'Literacy', score: 82 },
  { name: 'Sipho Ndlovu', subject: 'Mathematics', score: 91 },
  { name: 'Anika van der Merwe', subject: 'Life Skills', score: 75 },
  { name: 'Lerato Dlamini', subject: 'Literacy', score: 88 },
];

const AssessmentsMock: React.FC = () => (
  <div className="space-y-2">
    <div className="flex items-center justify-between mb-1">
      <h4 className="text-sm font-semibold text-gray-900 dark:text-white">Term 2 Assessments</h4>
      <span className="text-[10px] text-gray-400">CAPS Scale · 4 / 4</span>
    </div>
    <div className="rounded-lg bg-white dark:bg-gray-800 border border-gray-100 dark:border-gray-700 overflow-hidden">
      <div className="grid grid-cols-12 gap-2 px-3 py-1.5 text-[9px] uppercase tracking-wider text-gray-500 dark:text-gray-400 bg-gray-50 dark:bg-gray-900">
        <span className="col-span-5">Learner</span>
        <span className="col-span-3">Subject</span>
        <span className="col-span-3">Score</span>
        <span className="col-span-1 text-right">%</span>
      </div>
      {assessmentsRows.map(row => (
        <div
          key={row.name}
          className="grid grid-cols-12 gap-2 px-3 py-2 items-center border-t border-gray-100 dark:border-gray-700"
        >
          <span className="col-span-5 text-[10px] font-medium text-gray-900 dark:text-white truncate">
            {row.name}
          </span>
          <span className="col-span-3 text-[10px] text-gray-500 dark:text-gray-400 truncate">
            {row.subject}
          </span>
          <div className="col-span-3">
            <div className="h-1.5 rounded-full bg-gray-200 dark:bg-gray-700 overflow-hidden">
              <div
                className="h-full bg-gradient-to-r from-primary-500 to-secondary-500"
                style={{ width: `${row.score}%` }}
              />
            </div>
          </div>
          <span className="col-span-1 text-right text-[10px] font-semibold text-gray-900 dark:text-white">
            {row.score}
          </span>
        </div>
      ))}
    </div>
  </div>
);

const curriculumAreas = [
  { name: 'Home Language', outcomes: 8, expanded: true },
  { name: 'Mathematics', outcomes: 6, expanded: false },
  { name: 'Life Skills', outcomes: 7, expanded: false },
];

const CurriculumMock: React.FC = () => (
  <div className="space-y-2">
    <div className="flex items-center justify-between mb-1">
      <h4 className="text-sm font-semibold text-gray-900 dark:text-white">CAPS Curriculum · Grade 1</h4>
      <span className="text-[10px] text-gray-400">21 outcomes</span>
    </div>
    {curriculumAreas.map(area => (
      <div
        key={area.name}
        className="rounded-lg bg-white dark:bg-gray-800 border border-gray-100 dark:border-gray-700 overflow-hidden"
      >
        <div className="flex items-center justify-between px-3 py-2">
          <div className="flex items-center gap-2">
            <span
              className={`inline-block w-3 h-3 rounded-sm ${
                area.expanded ? 'bg-primary-500' : 'bg-gray-300 dark:bg-gray-600'
              }`}
              aria-hidden="true"
            />
            <p className="text-[11px] font-medium text-gray-900 dark:text-white">{area.name}</p>
          </div>
          <span className="text-[10px] text-gray-500 dark:text-gray-400">{area.outcomes} outcomes</span>
        </div>
        {area.expanded && (
          <div className="border-t border-gray-100 dark:border-gray-700 px-3 py-2 space-y-1.5">
            <div className="flex items-start gap-2">
              <span className="text-[9px] font-mono text-primary-600 dark:text-primary-400 mt-0.5">1.1</span>
              <p className="text-[10px] text-gray-600 dark:text-gray-300">
                Listens to and responds to stories in home language.
              </p>
            </div>
            <div className="flex items-start gap-2">
              <span className="text-[9px] font-mono text-primary-600 dark:text-primary-400 mt-0.5">1.2</span>
              <p className="text-[10px] text-gray-600 dark:text-gray-300">
                Recognises letter–sound relationships (phonics).
              </p>
            </div>
            <div className="flex items-start gap-2">
              <span className="text-[9px] font-mono text-primary-600 dark:text-primary-400 mt-0.5">1.3</span>
              <p className="text-[10px] text-gray-600 dark:text-gray-300">
                Reads simple texts with understanding.
              </p>
            </div>
          </div>
        )}
      </div>
    ))}
  </div>
);

const ReportsMock: React.FC = () => (
  <div className="h-full flex gap-3">
    {/* Sidebar list */}
    <div className="w-1/3 space-y-1.5">
      <div className="text-[9px] uppercase tracking-wider text-gray-500 dark:text-gray-400 font-semibold px-1 mb-1">
        Reports
      </div>
      {[
        { label: 'Term 2 Progress', active: true },
        { label: 'Parent Summary', active: false },
        { label: 'CAPS Overview', active: false },
        { label: 'Attendance', active: false },
      ].map(item => (
        <div
          key={item.label}
          className={`px-2 py-1.5 rounded text-[10px] ${
            item.active
              ? 'bg-primary-100 dark:bg-primary-900/40 text-primary-700 dark:text-primary-300 font-medium'
              : 'text-gray-600 dark:text-gray-300'
          }`}
        >
          {item.label}
        </div>
      ))}
    </div>

    {/* PDF preview */}
    <div className="flex-1 rounded-md bg-white dark:bg-gray-800 border border-gray-100 dark:border-gray-700 p-3 shadow-inner overflow-hidden">
      <div className="text-center border-b border-gray-100 dark:border-gray-700 pb-2 mb-2">
        <p className="text-[11px] font-bold text-gray-900 dark:text-white">Term 2 Progress Report</p>
        <p className="text-[9px] text-gray-500 dark:text-gray-400">Protea Primary · Grade 1B</p>
      </div>
      <div className="space-y-1.5 mb-2">
        <div className="h-1.5 w-3/4 bg-gray-200 dark:bg-gray-700 rounded" />
        <div className="h-1.5 w-5/6 bg-gray-200 dark:bg-gray-700 rounded" />
        <div className="h-1.5 w-2/3 bg-gray-200 dark:bg-gray-700 rounded" />
      </div>
      <div className="grid grid-cols-4 gap-1 items-end h-12">
        {[60, 85, 70, 90].map((h, i) => (
          <div key={i} className="flex flex-col items-center gap-0.5">
            <div
              className="w-full rounded-sm bg-gradient-to-t from-primary-500 to-secondary-400"
              style={{ height: `${h}%` }}
            />
            <span className="text-[7px] text-gray-400">T{i + 1}</span>
          </div>
        ))}
      </div>
    </div>
  </div>
);

const DashboardMock: React.FC = () => (
  <div className="space-y-2.5">
    <div className="flex items-center justify-between">
      <h4 className="text-sm font-semibold text-gray-900 dark:text-white">Today at Protea Primary</h4>
      <span className="text-[10px] text-gray-400">23 April 2026</span>
    </div>
    <div className="grid grid-cols-3 gap-2">
      {[
        { label: 'Children', value: '26', trend: '+2' },
        { label: 'Observations', value: '14', trend: 'today' },
        { label: 'Assessments', value: '8', trend: 'this week' },
      ].map(stat => (
        <div
          key={stat.label}
          className="rounded-lg bg-white dark:bg-gray-800 border border-gray-100 dark:border-gray-700 p-2.5"
        >
          <p className="text-[9px] uppercase tracking-wide text-gray-500 dark:text-gray-400">{stat.label}</p>
          <p className="text-lg font-display font-bold text-gray-900 dark:text-white mt-0.5">{stat.value}</p>
          <p className="text-[9px] text-emerald-600 dark:text-emerald-400 mt-0.5">{stat.trend}</p>
        </div>
      ))}
    </div>
    <div className="rounded-lg bg-white dark:bg-gray-800 border border-gray-100 dark:border-gray-700 p-2.5">
      <p className="text-[10px] font-semibold text-gray-900 dark:text-white mb-1.5">Observations this week</p>
      <div className="flex items-end gap-1.5 h-12">
        {[40, 65, 55, 80, 70, 90, 60].map((h, i) => (
          <div key={i} className="flex-1 flex flex-col items-center gap-0.5">
            <div
              className="w-full rounded-sm bg-gradient-to-t from-primary-400 to-secondary-400"
              style={{ height: `${h}%` }}
            />
            <span className="text-[7px] text-gray-400">
              {['M', 'T', 'W', 'T', 'F', 'S', 'S'][i]}
            </span>
          </div>
        ))}
      </div>
    </div>
  </div>
);

const GuardianMock: React.FC = () => (
  <div className="space-y-2">
    <div className="flex items-center justify-between mb-1">
      <h4 className="text-sm font-semibold text-gray-900 dark:text-white">My Children</h4>
      <span className="text-[10px] text-gray-400">Parent Portal</span>
    </div>
    {[
      {
        name: 'Thandi Mokoena',
        grade: 'Grade R',
        initials: 'TM',
        updates: 3,
        gradient: 'from-pink-400 to-rose-400',
      },
      {
        name: 'Sipho Mokoena',
        grade: 'Grade 2',
        initials: 'SM',
        updates: 1,
        gradient: 'from-blue-400 to-indigo-400',
      },
    ].map(child => (
      <div
        key={child.name}
        className="flex items-center gap-3 rounded-lg bg-white dark:bg-gray-800 border border-gray-100 dark:border-gray-700 p-2.5 shadow-sm"
      >
        <div
          className={`w-10 h-10 rounded-full bg-gradient-to-br ${child.gradient} flex items-center justify-center text-white text-xs font-semibold flex-shrink-0`}
        >
          {child.initials}
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-xs font-medium text-gray-900 dark:text-white truncate">{child.name}</p>
          <p className="text-[10px] text-gray-500 dark:text-gray-400">{child.grade} · Protea Primary</p>
        </div>
        <div className="flex-shrink-0">
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-primary-100 dark:bg-primary-900/40 text-[9px] font-medium text-primary-700 dark:text-primary-300">
            {child.updates} new
          </span>
        </div>
      </div>
    ))}
    <div className="rounded-lg bg-primary-50 dark:bg-primary-900/20 border border-primary-100 dark:border-primary-900/40 p-2.5">
      <p className="text-[10px] font-semibold text-primary-800 dark:text-primary-200">Latest update</p>
      <p className="text-[10px] text-primary-700 dark:text-primary-300 mt-0.5">
        Ms Mkhize shared a new observation for Thandi: "Completed a 12-piece puzzle independently..."
      </p>
    </div>
  </div>
);

export default MockupPreview;
